import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react'

import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'
import Avatar from '../Avatar/Avatar'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import StaffEditForm from './StaffEditForm'
import { requestCreator } from '../../../utilities/requests'

const { get: getStaffMember, cancel: cancelStaffMemberRequest } = requestCreator()

function StaffDrawer({ open, onClose, staffId = null, facilityId = null, onUpdate, onDelete, onActionError }) {
  const [staffMember, setStaffMember] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false)
  const [showDemoteConfirm, setShowDemoteConfirm] = useState(false)

  useEffect(() => {
    if (open && staffId && facilityId) {
      setIsLoading(true)
      setError(false)
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setShowPromoteConfirm(false)
      setShowDemoteConfirm(false)
      getStaffMember(`/api/facilities/${facilityId}/coaches`)
        .then((response) => {
          const coaches = response.data || response
          const selectedCoach = Array.isArray(coaches)
            ? coaches.find((c) => c.id === staffId.toString() || c.id === staffId)
            : null
          if (selectedCoach) {
            setStaffMember({ data: { id: selectedCoach.id, attributes: selectedCoach } })
            setIsLoading(false)
          } else {
            setError(true)
            setIsLoading(false)
          }
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setIsLoading(false)
            setError(true)
          }
        })
    }

    return () => {
      cancelStaffMemberRequest()
    }
  }, [open, staffId, facilityId])

  function handleClose() {
    setStaffMember(null)
    setError(false)
    setIsDeleting(false)
    setShowDeleteConfirm(false)
    setShowPromoteConfirm(false)
    setShowDemoteConfirm(false)
    onClose()
  }

  async function handleDelete() {
    setIsDeleting(true)
    try {
      await axios.delete(`/api/facilities/${facilityId}/coaches/${staffId}`)
      if (onDelete) {
        onDelete(staffId)
      }
      handleClose()
    } catch (err) {
      const errorMessage = err?.response?.data?.error || 'Could not remove staff member. Try again.'
      setShowDeleteConfirm(false)
      setIsDeleting(false)
      if (onActionError) {
        onActionError(errorMessage)
      } else {
        setError(true)
      }
    }
  }

  function handleUpdateSuccess(updatedStaff) {
    getStaffMember(`/api/facilities/${facilityId}/coaches`)
      .then((response) => {
        const coaches = response.data || response
        const selectedCoach = Array.isArray(coaches)
          ? coaches.find((c) => c.id === staffId.toString() || c.id === staffId)
          : null
        if (selectedCoach) {
          setStaffMember({ data: { id: selectedCoach.id, attributes: selectedCoach } })
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError(true)
        }
      })

    if (onUpdate) {
      onUpdate(updatedStaff.data || updatedStaff)
    }
  }

  async function confirmPromote() {
    const permissionLevel =
      (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.permissionLevel) ||
      (staffMember.attributes && staffMember.attributes.permissionLevel)
    const newPermissions = getPromotedPermissions(permissionLevel)
    try {
      const response = await axios.patch(`/api/facilities/${facilityId}/coaches/${staffId}`, {
        coach: newPermissions
      })
      setStaffMember(response.data)
      setShowPromoteConfirm(false)
      if (onUpdate) {
        onUpdate(response.data)
      }
    } catch (err) {
      setError(true)
      setShowPromoteConfirm(false)
    }
  }

  async function confirmDemote() {
    const permissionLevel =
      (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.permissionLevel) ||
      (staffMember.attributes && staffMember.attributes.permissionLevel)
    const newPermissions = getDemotedPermissions(permissionLevel)
    try {
      const response = await axios.patch(`/api/facilities/${facilityId}/coaches/${staffId}`, {
        coach: newPermissions
      })
      setStaffMember(response.data)
      setShowDemoteConfirm(false)
      if (onUpdate) {
        onUpdate(response.data)
      }
    } catch (err) {
      setError(true)
      setShowDemoteConfirm(false)
    }
  }

  function getPromotedPermissions(currentLevel) {
    switch (currentLevel) {
      case 'staff':
        return { admin: false, coach: true }
      case 'coach':
        return { admin: true, coach: true }
      default:
        return { admin: true, coach: true }
    }
  }

  function getDemotedPermissions(currentLevel) {
    switch (currentLevel) {
      case 'director':
        return { admin: false, coach: true }
      case 'coach':
        return { admin: false, coach: false }
      default:
        return { admin: false, coach: false }
    }
  }

  function canPromote(permissionLevel) {
    return permissionLevel !== 'director'
  }

  function canDemote(permissionLevel) {
    return permissionLevel !== 'staff'
  }

  function getBadgeVariant(permissionLevel) {
    switch (permissionLevel) {
      case 'director':
        return 'default'
      case 'coach':
        return 'secondary'
      case 'staff':
        return 'outline'
      default:
        return 'outline'
    }
  }

  function getPermissionLabel(permissionLevel) {
    switch (permissionLevel) {
      case 'director':
        return 'Director'
      case 'coach':
        return 'Coach'
      case 'staff':
        return 'Staff'
      default:
        return 'Unknown'
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <LoadingSpinner size="1/5" />
        </div>
      )
    }

    if (error || !staffMember) {
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gray-100">
              <AlertTriangle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {error ? 'Unable to Load Staff Member' : 'No Staff Information'}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {error
                ? 'There was an error loading the staff member details. Please try again or contact support if the problem persists.'
                : 'No information is available for this staff member at the moment.'}
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button variant="outline" onClick={handleClose} size="lg">
              Close
            </Button>
          </DrawerFooter>
        </>
      )
    }

    if (showDeleteConfirm) {
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Remove Staff Member?</h3>
            <p className="text-sm text-gray-500 max-w-sm mb-6">
              Are you sure you want to remove{' '}
              {(staffMember.data && staffMember.data.attributes && staffMember.data.attributes.fullName) ||
                (staffMember.attributes && staffMember.attributes.fullName) ||
                (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.preferredName) ||
                (staffMember.attributes && staffMember.attributes.preferredName)}{' '}
              from this facility? This action cannot be undone.
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button variant="destructive" onClick={handleDelete} size="lg" disabled={isDeleting}>
              {isDeleting ? 'Removing...' : 'Yes, Remove Staff Member'}
            </Button>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} size="lg" disabled={isDeleting}>
              Cancel
            </Button>
          </DrawerFooter>
        </>
      )
    }

    if (showPromoteConfirm) {
      const permissionLevel =
        (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.permissionLevel) ||
        (staffMember.attributes && staffMember.attributes.permissionLevel)
      const newLevel = getPermissionLevelFromFlags(getPromotedPermissions(permissionLevel))
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-blue-100">
              <ChevronUp className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Promote Staff Member?</h3>
            <p className="text-sm text-gray-500 max-w-sm mb-6">
              Are you sure you want to promote{' '}
              {(staffMember.data && staffMember.data.attributes && staffMember.data.attributes.fullName) ||
                (staffMember.attributes && staffMember.attributes.fullName) ||
                (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.preferredName) ||
                (staffMember.attributes && staffMember.attributes.preferredName)}{' '}
              to {getPermissionLabel(newLevel)}?
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button onClick={confirmPromote} size="lg">
              Yes, Promote to {getPermissionLabel(newLevel)}
            </Button>
            <Button variant="outline" onClick={() => setShowPromoteConfirm(false)} size="lg">
              Cancel
            </Button>
          </DrawerFooter>
        </>
      )
    }

    if (showDemoteConfirm) {
      const permissionLevel =
        (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.permissionLevel) ||
        (staffMember.attributes && staffMember.attributes.permissionLevel)
      const newLevel = getPermissionLevelFromFlags(getDemotedPermissions(permissionLevel))
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-orange-100">
              <ChevronDown className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Demote Staff Member?</h3>
            <p className="text-sm text-gray-500 max-w-sm mb-6">
              Are you sure you want to demote{' '}
              {(staffMember.data && staffMember.data.attributes && staffMember.data.attributes.fullName) ||
                (staffMember.attributes && staffMember.attributes.fullName) ||
                (staffMember.data && staffMember.data.attributes && staffMember.data.attributes.preferredName) ||
                (staffMember.attributes && staffMember.attributes.preferredName)}{' '}
              to {getPermissionLabel(newLevel)}?
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button onClick={confirmDemote} size="lg">
              Yes, Demote to {getPermissionLabel(newLevel)}
            </Button>
            <Button variant="outline" onClick={() => setShowDemoteConfirm(false)} size="lg">
              Cancel
            </Button>
          </DrawerFooter>
        </>
      )
    }

    function getPermissionLevelFromFlags(flags) {
      if (flags.admin) return 'director'
      if (flags.coach) return 'coach'
      return 'staff'
    }

    const { fullName, preferredName, cloudinaryPhotoPublicId, permissionLevel } =
      (staffMember.data && staffMember.data.attributes) || staffMember.attributes || {}

    return (
      <>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col items-center space-y-3 mb-6 pb-6 border-b border-gray-200">
            <Avatar
              fullName={fullName || preferredName}
              cloudinaryPhotoPublicId={cloudinaryPhotoPublicId}
              alt={`Photo of ${fullName || preferredName}`}
              userId={(staffMember.data && staffMember.data.id) || staffMember.id}
              size={24}
              className=""
            />
            <Badge variant={getBadgeVariant(permissionLevel)}>{getPermissionLabel(permissionLevel)}</Badge>
          </div>

          <StaffEditForm staffMember={staffMember} facilityId={facilityId} onSuccess={handleUpdateSuccess} />
        </div>

        <DrawerFooter className="border-t border-gray-200 space-y-2">
          <div className="flex gap-2 w-full">
            {canPromote(permissionLevel) && (
              <Button variant="outline" onClick={() => setShowPromoteConfirm(true)} size="lg" className="flex-1">
                <ChevronUp className="w-4 h-4 mr-2" />
                Promote
              </Button>
            )}
            {canDemote(permissionLevel) && (
              <Button variant="outline" onClick={() => setShowDemoteConfirm(true)} size="lg" className="flex-1">
                <ChevronDown className="w-4 h-4 mr-2" />
                Demote
              </Button>
            )}
          </div>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} size="lg" className="w-full">
            Remove from Facility
          </Button>
          <Button variant="outline" onClick={handleClose} size="lg" className="w-full">
            Close
          </Button>
        </DrawerFooter>
      </>
    )
  }

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} direction="right">
      <DrawerContent className="max-h-[96vh] md:max-h-screen">
        <DrawerHeader className="border-b border-gray-200">
          <DrawerTitle className="text-xl">Staff Member Details</DrawerTitle>
          <DrawerDescription>View and manage staff member information</DrawerDescription>
        </DrawerHeader>
        {renderContent()}
      </DrawerContent>
    </Drawer>
  )
}

StaffDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  staffId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onActionError: PropTypes.func
}

export default StaffDrawer
